"use client";

import { useEffect, useState, type ReactNode } from "react";
import { useTranslations } from "next-intl";
import { motion, Variants } from "framer-motion";
import { joinClassNames } from "@utils/helpers";
import { formatWait } from "@utils/queue";
import { Heading, Text } from "@components/theming";
import LoadingSpinner from "@components/loadingSpinner";
import { GateState, QueueStatus } from "./types";
import QueueStat from "./queueStat";
import QueueCountdown from "./queueCountdown";
import TicketGateCard from "./ticketGateCard";
import { useIsShopsite } from "@context/DomainContext";
import { useThemeContext } from "@utils/hooks";

const variants: Variants = {
  initial: { opacity: 0, transition: { duration: 0.2 } },
  animate: { opacity: 1, transition: { duration: 0.2 } },
  exit: { opacity: 0, transition: { duration: 0.2 } },
};

/**
 * In-page waiting-room gate. Wraps ONLY the ticket-purchase section of an event
 * page. The rest of the page stays browsable at all times.
 *
 * Lifecycle (inline mode):
 *   before opensAt → countdown ("On sale at …"), tickets hidden
 *   at opensAt     → auto-swaps to the queue and polls /api/queue/status
 *   on admission   → renders `children` (the real ticket UI) in place
 *
 * Fails open: any config/network error renders `children` so the gate can never
 * block legitimate selling.
 */
export default function TicketGate({
  eventName,
  children,
  queueState,
  setQueueState,
  queueStatus,
  admittedRef,
  opensAt,
  hasSubdescription,
}: {
  eventName?: string;
  children: ReactNode;
  queueState: GateState;
  setQueueState: (state: GateState) => void;
  queueStatus: QueueStatus;
  admittedRef: React.RefObject<boolean>;
  opensAt: number | null;
  hasSubdescription?: boolean;
}) {
  const t = useTranslations("Queue");

  const [now, setNow] = useState<number>(() => Date.now());
  const isShopsite = useIsShopsite();
  const theme = useThemeContext();

  // Tick the clock while browsing so the countdown updates and we can flip
  // into the queue the moment opensAt passes.
  useEffect(() => {
    if (queueState !== "browse") return;
    const id = setInterval(() => {
      const currTime = Date.now();
      setNow(currTime);
      if (opensAt !== null && currTime >= opensAt && !admittedRef.current) {
        setTimeout(() => {
          if (!admittedRef.current) setQueueState("queue");
        }, Math.random() * 1000); // jitter
      }
    }, 1000);
    return () => clearInterval(id);
  }, [queueState, opensAt, admittedRef, setQueueState]);

  const ahead = queueStatus.ahead;
  const waitSeconds = queueStatus.estimatedWaitSeconds;

  return queueState === "loading" ? (
    <motion.div
      key="loading"
      variants={variants}
      initial="initial"
      animate="animate"
      exit="exit"
      className={joinClassNames(
        "flex h-48 items-center justify-center",
        isShopsite && hasSubdescription ? "mt-10" : "mt-4"
      )}
    >
      <LoadingSpinner
        size="32"
        color={theme && theme.appearance === "dark" ? "#fff" : undefined}
      />
      <span className="sr-only">{t("loadingAvailability")}</span>
    </motion.div>
  ) : queueState === "open" || queueState === "admitted" ? (
    <motion.div
      key="open"
      variants={variants}
      initial="initial"
      animate="animate"
      exit="exit"
    >
      {children}
    </motion.div>
  ) : queueState === "browse" ? (
    <motion.div
      key="browse"
      variants={variants}
      initial="initial"
      animate="animate"
      exit="exit"
    >
      <TicketGateCard hasSubdescription={hasSubdescription}>
        <Text
          as="p"
          sharedClasses="uppercase"
          weight="bold"
          whitelabelClasses="text-[color:var(--accent-9)]"
          shopsiteClasses="text-sm font-semibold uppercase text-iticket"
        >
          {t("onSaleSoon")}
        </Text>
        <Heading
          as="h3"
          sharedClasses="mt-2 text-balance tracking-tight"
          shopsiteClasses="text-2xl font-black text-black"
        >
          {eventName ?? t("thisEvent")}
        </Heading>
        <Text
          as="p"
          sharedClasses="mt-2 text-pretty"
          shopsiteClasses="text-gray-500"
          whitelabelClasses="text-[color:var(--gray-11)]"
        >
          {t("browseSubtitle")}
        </Text>
        <QueueCountdown msRemaining={Math.max(0, (opensAt ?? 0) - now)} t={t} />
      </TicketGateCard>
    </motion.div>
  ) : (
    <motion.div
      key="queue"
      variants={variants}
      initial="initial"
      animate="animate"
      exit="exit"
    >
      <TicketGateCard hasSubdescription={hasSubdescription}>
        <div className="flex flex-col items-center text-center">
          <div
            className={joinClassNames(
              "mb-5 flex h-14 w-14 items-center justify-center rounded-full",
              isShopsite ? "bg-iticket-light" : "bg-[color:var(--accent-a3)]"
            )}
          >
            <span
              className={joinClassNames(
                "block h-7 w-7 animate-spin rounded-full border-[3px] border-transparent",
                isShopsite
                  ? "border-iticket-light border-t-iticket"
                  : "border-t-[color:var(--accent-9)]"
              )}
              aria-hidden="true"
            />
          </div>
          <Heading
            as="h3"
            shopsiteClasses="text-2xl font-black tracking-tight text-black"
          >
            {t("inQueueTitle")}
          </Heading>
          {eventName ? (
            <Text
              as="p"
              weight="bold"
              sharedClasses="mt-1"
              shopsiteClasses="font-semibold text-iticket"
            >
              {eventName}
            </Text>
          ) : null}
          <Text
            as="p"
            sharedClasses="mt-3"
            shopsiteClasses="text-gray-500"
            whitelabelClasses="text-[color:var(--gray-11)]"
          >
            {queueStatus.state === "error"
              ? t("reconnectingInline")
              : t("highDemandInline")}
          </Text>
          <div className="mt-7 w-full">
            {queueStatus.state !== "error" && (
              <div className="mt-6 grid grid-cols-2 gap-4">
                {ahead === 0 && queueStatus.state === "waiting" ? (
                  <QueueStat value={t("youreNext")} />
                ) : (
                  <QueueStat
                    label={t("peopleAhead")}
                    value={
                      typeof ahead === "number" ? ahead.toLocaleString() : "-"
                    }
                  />
                )}
                <QueueStat
                  label={t("estimatedWait")}
                  value={
                    waitSeconds !== undefined && waitSeconds !== null
                      ? formatWait(waitSeconds, t)
                      : "-"
                  }
                />
              </div>
            )}
          </div>
          <Text
            as="p"
            sharedClasses="mt-7 text-sm leading-relaxed"
            shopsiteClasses="text-gray-400"
            whitelabelClasses="text-[color:var(--gray-11)]"
          >
            {t("doNotRefreshInline")}
          </Text>
        </div>
      </TicketGateCard>
    </motion.div>
  );
}
